// Re-sincroniza com o Supabase quando o app volta ao foco:
//   - visibilitychange (mobile: app sai/volta do background)
//   - focus (desktop: aba ganha foco)
//   - online (volta a ter rede)
//
// Sem isso, o app só sincronizava uma vez no boot. Se a vendedora
// deixava o MINI Deck aberto no celular o dia todo, ela não via os
// pedidos que outras pessoas cadastravam — precisava fechar/reabrir.
//
// Após cada sync bem-sucedido dispara um evento window 'mini:resynced'
// que o useOrders escuta pra re-renderizar a UI.

import { useEffect, useRef } from 'react'
import { syncFromSupabase } from '../utils/supabase'

// Tempo mínimo entre syncs (evita flood ao alternar entre apps rapidamente)
const MIN_INTERVALO_MS = 5_000

export function useAutoSync({ enabled = true } = {}) {
  const ultimaSyncRef = useRef(0)
  const emCursoRef    = useRef(false)

  useEffect(() => {
    if (!enabled) return

    async function tentarSync(motivo) {
      // Throttle: ignora se já sincronizou recentemente
      const agora = Date.now()
      if (agora - ultimaSyncRef.current < MIN_INTERVALO_MS) return
      // Sem concorrência
      if (emCursoRef.current) return
      // Sem rede: não vale a pena tentar
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return

      emCursoRef.current = true
      try {
        const ok = await syncFromSupabase()
        if (ok) {
          ultimaSyncRef.current = Date.now()
          // Notifica componentes que dependem de dados (useOrders etc.)
          window.dispatchEvent(new CustomEvent('mini:resynced', { detail: { motivo } }))
        }
      } finally {
        emCursoRef.current = false
      }
    }

    function onVisibilityChange() {
      if (document.visibilityState === 'visible') tentarSync('visibilitychange')
    }
    function onFocus()  { tentarSync('focus')  }
    function onOnline() { tentarSync('online') }

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('focus', onFocus)
    window.addEventListener('online', onOnline)

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('online', onOnline)
    }
  }, [enabled])
}
